import { useCallback, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useSWR from 'swr';
import { useEventSlug } from '@/hooks/use-event-slug';
import { formatPercent } from './format';
import type { TradeMessage } from './live-state';
import { historyKey, useMarketFeed, useMarketTrades } from './live';
import { RollingNumber } from './rolling-number';

const fetcher = (url: string) => fetch(url).then((r) => r.json());

type Point = { t: number; price: number };

// The history route replays every trade, so it is read once (and again after a
// reconnect); the trades after that are appended from the live feed, and the line ends
// at the live price rather than at the route's own "now".
export function PriceChart({
  projectId,
  livePrice,
  open,
}: {
  projectId: string;
  livePrice: number;
  open: boolean;
}) {
  const eventSlug = useEventSlug();
  const live = useMarketFeed(eventSlug);
  const { data } = useSWR<{ points: Array<{ t: string; price: number }> }>(
    historyKey(eventSlug, projectId),
    fetcher,
    {
      refreshInterval: live ? 0 : 30000,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
    },
  );

  const [livePoints, setLivePoints] = useState<Point[]>([]);
  const onTrade = useCallback(
    (trade: TradeMessage) => {
      const price = trade.prices[projectId];
      if (price === undefined) return;
      setLivePoints((current) => [
        ...current,
        { t: new Date(trade.at).getTime(), price },
      ]);
    },
    [projectId],
  );
  useMarketTrades(eventSlug, onTrade);

  const points: Point[] = useMemo(() => {
    const fetched =
      data?.points?.map((p) => ({
        t: new Date(p.t).getTime(),
        price: p.price,
      })) ?? [];
    if (!open || fetched.length === 0) return fetched;

    // Everything but the route's tail, then whatever traded since its last snapshot.
    const history = fetched.slice(0, -1);
    const lastT = history[history.length - 1]?.t ?? 0;
    const appended = livePoints
      .filter((p) => p.t > lastT)
      .sort((a, b) => a.t - b.t);
    const tailT = Math.max(Date.now(), appended[appended.length - 1]?.t ?? 0);
    return [...history, ...appended, { t: tailT, price: livePrice }];
  }, [data, livePoints, livePrice, open]);

  const stats = useMemo(() => {
    if (points.length === 0) return null;
    const first = points[0].price;
    const last = points[points.length - 1].price;
    const high = Math.max(...points.map((p) => p.price));
    const low = Math.min(...points.map((p) => p.price));
    const change = last - first;
    const changePct = first > 0 ? (change / first) * 100 : 0;
    return { first, last, high, low, change, changePct };
  }, [points]);

  if (points.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center border-2 border-primary/30 border-dashed">
        <p className="font-mono text-primary/60 text-xs uppercase tracking-wider">
          Sin trades aún
        </p>
      </div>
    );
  }

  const positive = (stats?.change ?? 0) >= 0;
  const lineColor = positive
    ? 'hsl(var(--primary))'
    : 'hsl(var(--destructive))';
  const yMax = Math.min(1, Math.max(0.05, (stats?.high ?? 0) * 1.2));
  const yMin = Math.max(0, (stats?.low ?? 0) * 0.5);

  return (
    <div className="flex flex-col gap-4">
      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat
            label="Actual"
            value={<RollingNumber value={stats.last} format={formatPercent} />}
          />
          <Stat
            label="Cambio"
            value={`${positive ? '+' : ''}${stats.changePct.toFixed(1)}%`}
            valueClass={positive ? 'text-green-600' : 'text-red-600'}
          />
          <Stat label="Máx" value={formatPercent(stats.high)} />
          <Stat label="Mín" value={formatPercent(stats.low)} />
        </div>
      )}

      <div className="border-2 border-primary bg-background p-3">
        <div className="h-56 w-full sm:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={points}
              margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lineColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="2 4"
                stroke="hsl(var(--primary))"
                opacity={0.15}
                vertical={false}
              />
              <XAxis
                dataKey="t"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(v) =>
                  new Date(v).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }
                tickLine={false}
                axisLine={{ stroke: 'hsl(var(--primary))', opacity: 0.3 }}
                tick={{
                  fill: 'hsl(var(--primary))',
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                }}
                minTickGap={40}
              />
              <YAxis
                domain={[yMin, yMax]}
                tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                tickLine={false}
                axisLine={false}
                tick={{
                  fill: 'hsl(var(--primary))',
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                }}
                width={36}
              />
              {stats && (
                <ReferenceLine
                  y={stats.first}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="2 2"
                  strokeOpacity={0.4}
                />
              )}
              <Tooltip
                cursor={{
                  stroke: 'hsl(var(--primary))',
                  strokeWidth: 1,
                  strokeDasharray: '2 2',
                }}
                contentStyle={{
                  background: 'hsl(var(--background))',
                  border: '2px solid hsl(var(--primary))',
                  borderRadius: 0,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  padding: '8px 10px',
                }}
                labelStyle={{
                  color: 'hsl(var(--primary))',
                  opacity: 0.6,
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 4,
                }}
                itemStyle={{ color: 'hsl(var(--primary))', padding: 0 }}
                formatter={(v) => [formatPercent(Number(v)), 'Probabilidad']}
                labelFormatter={(v) => new Date(v as number).toLocaleString()}
              />
              <Area
                type="monotone"
                dataKey="price"
                stroke={lineColor}
                strokeWidth={2}
                fill="url(#priceFill)"
                dot={false}
                activeDot={{
                  r: 4,
                  fill: 'hsl(var(--background))',
                  stroke: lineColor,
                  strokeWidth: 2,
                }}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) {
  return (
    <div className="border-primary border-l-4 pl-3">
      <p className="font-mono text-[10px] text-primary/60 uppercase tracking-wider">
        {label}
      </p>
      <p
        className={`font-bold font-mono text-lg text-primary tabular-nums ${valueClass ?? ''}`}
      >
        {value}
      </p>
    </div>
  );
}
